import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie, Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { language, t } = useLanguage();

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Delay showing the banner for better UX
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      essential: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    closeBanner();
  };

  const handleEssentialOnly = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      essential: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    closeBanner();
  };

  const closeBanner = () => {
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-300 ${
        isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      data-testid="cookie-consent-banner"
    >
      {/* Backdrop blur overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none" />
      
      {/* Main banner */}
      <div className="relative mx-4 mb-4 lg:mx-8 lg:mb-6">
        <div 
          className="bg-[#003366] rounded-xl shadow-2xl border border-[#D4AF37]/30 overflow-hidden"
          style={{
            boxShadow: '0 -10px 40px rgba(0,51,102,0.3), 0 0 60px rgba(212,175,55,0.1)'
          }}
        >
          {/* Golden glow line at top */}
          <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
          
          <div className="p-4 lg:p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Icon and text */}
              <div className="flex items-start gap-4 flex-1">
                <div className="hidden sm:flex items-center justify-center w-12 h-12 rounded-full bg-[#D4AF37]/20 flex-shrink-0">
                  <Cookie className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold text-lg mb-1 flex items-center gap-2">
                    <Cookie className="w-5 h-5 text-[#D4AF37] sm:hidden" />
                    {t('title', translations.cookie)}
                  </h3>
                  <p className="text-white/70 text-sm leading-relaxed">
                    {t('message', translations.cookie)}
                  </p>
                  <Link 
                    to="/datenschutz" 
                    className="inline-flex items-center gap-1 text-[#D4AF37] text-sm mt-2 hover:underline"
                  >
                    <Shield className="w-3.5 h-3.5" />
                    {t('moreInfo', translations.cookie)}
                  </Link>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 lg:flex-shrink-0">
                <button
                  onClick={handleEssentialOnly}
                  className="px-5 py-2.5 rounded-lg border border-white/30 text-white text-sm font-medium hover:bg-white/10 transition-all duration-200"
                  data-testid="cookie-essential-only-btn"
                >
                  {t('essentialOnly', translations.cookie)}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-5 py-2.5 rounded-lg bg-[#D4AF37] text-[#003366] text-sm font-semibold hover:bg-[#c9a432] transition-all duration-200 shadow-lg hover:shadow-xl"
                  data-testid="cookie-accept-all-btn"
                >
                  {t('acceptAll', translations.cookie)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
